import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Toaster } from 'react-hot-toast';

// Pages
import Landing from './pages/Landing.jsx';
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import Dashboard from './pages/Dashboard.jsx';
import EventUpdates from './pages/EventUpdates.jsx';
import CreateNewShipment from './pages/CreateNewShipment.jsx';
import EditManifest from './pages/EditManifest.jsx';

// CRUD Pages
import RolesPage from './pages/crud/RolesPage.jsx';
import OriginsPage from './pages/crud/OriginsPage.jsx';
import DestinationsPage from './pages/crud/DestinationsPage.jsx';
import SuppliersPage from './pages/crud/SuppliersPage.jsx';
import DefaultNotificationsPage from './pages/crud/DefaultNotificationsPage.jsx';
import UsersManagementPage from './pages/admin/UsersManagementPage.jsx';

// Components
import PrivateRoute from './components/auth/PrivateRoute.jsx';
import PublicRoute from './components/auth/PublicRoute.jsx';
import AlertSystem from './components/ui/AlertSystem.jsx';

// Redux actions
import { initializeAuth } from './store/slices/authSlice.js';
import { initializeUI } from './store/slices/uiSlice.js';

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Initialize app state from localStorage
    dispatch(initializeAuth());
    dispatch(initializeUI());
  }, [dispatch]);

  return (
    <div className="App">
      {/* Alert System - Global notifications */}
      <AlertSystem />
      
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#059669',
            },
          },
          error: {
            style: {
              background: '#dc2626',
            },
          },
        }}
      />
      
      <Routes>
        {/* Public routes - accessible to everyone */}
        <Route path="/" element={<Landing />} />
        
        {/* Auth routes - only accessible when not authenticated */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />

        {/* Private routes - only accessible when authenticated */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />

        {/* CRUD Admin Routes */}
        <Route
          path="/dashboard/admin/roles"
          element={
            <PrivateRoute requiredRole="Administrador">
              <RolesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/admin/origins"
          element={
            <PrivateRoute requiredRole="Administrador">
              <OriginsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/admin/destinations"
          element={
            <PrivateRoute requiredRole="Administrador">
              <DestinationsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/admin/suppliers"
          element={
            <PrivateRoute requiredRole="Administrador">
              <SuppliersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/admin/default-notifications"
          element={
            <PrivateRoute requiredRole="Administrador">
              <DefaultNotificationsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/admin/users"
          element={
            <PrivateRoute requiredRole="Administrador">
              <UsersManagementPage />
            </PrivateRoute>
          }
        />

        {/* Event Updates Route - Accessible by Admin and Transportista */}
        <Route
          path="/dashboard/actualizar-estados"
          element={
            <PrivateRoute requiredRoles={["Administrador", "Transportista"]}>
              <EventUpdates />
            </PrivateRoute>
          }
        />

        {/* Create New Shipment Route - Accessible by Admin and Transportista */}
        <Route
          path="/dashboard/nuevo-envio"
          element={
            <PrivateRoute requiredRoles={["Administrador", "Transportista"]}>
              <CreateNewShipment />
            </PrivateRoute>
          }
        />

        {/* Edit Manifest Route - Accessible by Admin and Transportista */}
        <Route
          path="/dashboard/editar-manifiesto"
          element={
            <PrivateRoute requiredRoles={["Administrador", "Transportista"]}>
              <EditManifest />
            </PrivateRoute>
          }
        />

        {/* Default redirects */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;